// biome-ignore-all lint/suspicious/useIterableCallbackReturn: TODO - address later
import { useEffect, useMemo, useRef } from "react";
import { useUpdateDeepResearchReport } from "@/hooks/api/use-deep-research";
import type { CustomFieldDefinition } from "@/types/custom-fields";
import type { DeepResearchReportMetadata } from "@/types/deep-research-reports";
import type { UnifiedTableColumn } from "@/types/table-column";

// Maximum number of AI field generations allowed (records × AI fields)
export const MAX_GENERATIONS_LIMIT = 5000;

export function getReportFilters(report: DeepResearchReportMetadata) {
  const filters = report.row_filters;
  if (filters && typeof filters === "object" && !Array.isArray(filters)) {
    if ("operator" in filters && "filters" in filters) {
      // Caller will convert using convertToClientFilters; keep structure here
      return filters as Record<string, unknown>;
    }
    return filters as Record<string, unknown>;
  }
  return {} as Record<string, unknown>;
}

export function toStringParams(
  params: Record<string, unknown>,
): Record<string, string | undefined> {
  const out: Record<string, string | undefined> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      out[key] = undefined;
    } else if (typeof value === "object") {
      out[key] = JSON.stringify(value);
    } else {
      out[key] = String(value);
    }
  });
  return out;
}

export function toFilterColumns<T>(
  fullColumns: UnifiedTableColumn<T>[],
): { key: string; title: string; filterType?: string }[] {
  return fullColumns.map((col) => ({
    key: col.key as string,
    title: col.title,
    filterType: col.filterType as string | undefined,
  }));
}

export function useDrrAIFieldsSync(
  mode: "editor" | "viewer",
  report: DeepResearchReportMetadata,
  selectedCustomFields: CustomFieldDefinition[],
) {
  const updateReportMutation = useUpdateDeepResearchReport();
  const lastSubmittedAIIdsRef = useRef<string>("");

  const selectedAIFieldIds = useMemo(
    () =>
      selectedCustomFields
        .map((f) => f.id)
        .filter((id) => id.startsWith("aifd_")),
    [selectedCustomFields],
  );

  useEffect(() => {
    if (mode !== "editor" || report.status !== "draft") return;

    // Determine baseline current IDs from server state, falling back to last submitted
    const serverIds = (report.ai_fields || [])
      .map((f) => f.id)
      .filter((id) => id.startsWith("aifd_"));
    const lastSubmitted = lastSubmittedAIIdsRef.current
      ? lastSubmittedAIIdsRef.current.split(",").filter(Boolean)
      : [];
    const baselineIds = serverIds.length > 0 ? serverIds : lastSubmitted;

    const sortedCurrent = [...baselineIds].sort();
    const sortedSelected = [...selectedAIFieldIds].sort();

    const isEqual =
      sortedCurrent.length === sortedSelected.length &&
      sortedCurrent.every((id, i) => id === sortedSelected[i]);
    if (isEqual) return;

    const payloadKey = sortedSelected.join(",");
    if (lastSubmittedAIIdsRef.current === payloadKey) return;
    lastSubmittedAIIdsRef.current = payloadKey;

    updateReportMutation.mutate({
      id: report.id,
      visible_ai_field_ids: selectedAIFieldIds,
    });
  }, [
    mode,
    report.status,
    report.id,
    report.ai_fields,
    selectedAIFieldIds,
    updateReportMutation,
  ]);
}

export function useDrrRequiredColumns(
  reportFilters: Record<string, unknown>,
  userFilters: Record<string, unknown>,
) {
  const requiredColumnIds = useMemo(() => {
    const ids = new Set<string>();
    const collectKeys = (obj?: Record<string, unknown>) =>
      Object.keys(obj || {}).filter(
        (k) => k !== "sortColumn" && k !== "sortDirection" && k !== "search",
      );
    collectKeys(reportFilters).forEach((k) => ids.add(k));
    collectKeys(userFilters).forEach((k) => ids.add(k));
    return Array.from(ids);
  }, [reportFilters, userFilters]);

  const computeSelectionReady = (isColumnSelected: (id: string) => boolean) => {
    return requiredColumnIds.every((id) => isColumnSelected(id));
  };

  return { requiredColumnIds, computeSelectionReady };
}

export function ensureRequiredSelected(
  requiredIds: string[],
  columnsState: {
    isColumnSelected: (id: string) => boolean;
    toggleColumn: (id: string) => void;
  },
) {
  requiredIds.forEach((id) => {
    if (!columnsState.isColumnSelected(id)) {
      columnsState.toggleColumn(id);
    }
  });
}

// New helpers: compute and apply desired report columns (AI + filtered)
export function computeReportDesiredColumnIds(
  report: DeepResearchReportMetadata,
  reportFilters: Record<string, unknown>,
  userFilters: Record<string, unknown>,
  options?: { includeAI?: boolean },
): string[] {
  const includeAI = options?.includeAI === true;
  const ids = new Set<string>();

  if (includeAI) {
    (report.ai_fields || []).forEach((f) => ids.add(f.id));
  }

  const collectKeys = (obj?: Record<string, unknown>) =>
    Object.keys(obj || {}).filter(
      (k) => k !== "sortColumn" && k !== "sortDirection" && k !== "search",
    );
  collectKeys(reportFilters).forEach((k) => ids.add(k));
  collectKeys(userFilters).forEach((k) => ids.add(k));

  return Array.from(ids);
}

export function areAllColumnsSelected(
  ids: string[],
  isColumnSelected: (id: string) => boolean,
): boolean {
  return ids.every((id) => isColumnSelected(id));
}

export function ensureReportColumnsSelected(
  report: DeepResearchReportMetadata,
  reportFilters: Record<string, unknown>,
  userFilters: Record<string, unknown>,
  columnsState: {
    isColumnSelected: (id: string) => boolean;
    toggleColumn: (id: string) => void;
  },
  options?: { includeAI?: boolean },
) {
  const ids = computeReportDesiredColumnIds(
    report,
    reportFilters,
    userFilters,
    options,
  );
  ensureRequiredSelected(ids, columnsState);
}
